import { useMemo } from 'react';
import { T } from 'gt-react';
import { LineItemProductInfo } from '@/components/line-item-product-info/LineItemProductInfo';
import {
  useTable,
  appTableFeatures,
  flexRender,
  type ColumnDef,
} from '@/lib/tanstack-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TroubleDataBadges } from '@/components/common/TroubleDataBadges';
import type { PurchaseOrderLineItem } from '@erp/rpc';

interface PurchaseOrderLineItemsProps {
  lineItems: PurchaseOrderLineItem[];
  currency?: string | null;
}

const formatCurrency = (value: string | null | undefined, currency = 'USD') => {
  if (!value) return '—';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '—';

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

function formatQuantity(
  value: string | number | null | undefined,
  uom?: string | null,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }

  // Format with max 4 decimal places and truncate trailing zeros
  const formatted = numeric.toFixed(4);
  // Remove trailing zeros and unnecessary decimal point
  const quantityStr = formatted.replace(/\.?0+$/, '');
  return uom ? `${quantityStr} ${uom}` : quantityStr;
}

/**
 * Displays purchase order line items in a table.
 * Header charges should be displayed separately using ChargesTable.
 */
export function PurchaseOrderLineItems({
  lineItems,
  currency = 'USD',
}: PurchaseOrderLineItemsProps) {
  // Define columns
  const columns = useMemo<ColumnDef<PurchaseOrderLineItem>[]>(
    () => [
      {
        id: 'lineNumber',
        accessorKey: 'lineNumber',
        header: () => <div className="text-center">#</div>,
        cell: (info) => (
          <div className="text-center font-medium text-muted-foreground">
            {info.getValue() as number}
          </div>
        ),
        size: 48,
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Product',
        cell: (info) => {
          const row = info.row.original;
          const description = row.description ?? null;
          const hasProductInfo = !!(row.productId || row.productName);
          return (
            <div className="flex items-start gap-2">
              <div className="flex flex-col min-w-0 flex-1 gap-1">
                {hasProductInfo ? (
                  <>
                    <LineItemProductInfo
                      productId={row.productId}
                      productVariantId={row.productVariantId}
                      productName={row.productName}
                      productSku={row.productSku}
                      productVariantSku={row.productVariantSku}
                    />
                    {description && (
                      <span className="text-xs text-muted-foreground">
                        {description}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="font-medium text-sm">
                    {description || <T>No description</T>}
                  </span>
                )}
              </div>
              <TroubleDataBadges
                troubleData={row.troubleData}
                legacyHoldPay={row.paymentHold ?? false}
                size="md"
              />
            </div>
          );
        },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: () => (
          <div className="text-right">
            <T>Qty</T>
          </div>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="text-right">
              {formatQuantity(row.quantity, row.uom)}
            </div>
          );
        },
        size: 100,
      },
      {
        id: 'unitPrice',
        accessorKey: 'unitPrice',
        header: () => (
          <div className="text-right">
            <T>Unit Price</T>
          </div>
        ),
        cell: (info) => (
          <div className="text-right text-muted-foreground">
            {formatCurrency(
              info.getValue() as string | null,
              currency ?? undefined,
            )}
          </div>
        ),
        size: 112,
      },
      {
        id: 'totalPrice',
        accessorKey: 'totalPrice',
        header: () => (
          <div className="text-right">
            <T>Total</T>
          </div>
        ),
        cell: (info) => (
          <div className="text-right font-semibold">
            {formatCurrency(
              info.getValue() as string | null,
              currency ?? undefined,
            )}
          </div>
        ),
        size: 112,
      },
    ],
    [currency],
  );

  const table = useTable({
    features: appTableFeatures,
    data: lineItems,
    columns,
  });

  return (
    <div className="space-y-2">
      <div className="border rounded-md mx-3 overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-muted/50 backdrop-blur">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{
                        width:
                          header.getSize() !== 150
                            ? `${header.getSize()}px`
                            : undefined,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
